<template>
	<div class="qr-scanner">
		<ModalHeader :title="getString('scan_qr_code')" />
		<div class="scanner-container">
			<template v-if="usingNativeScanner">
				<div class="qr-icon" v-html="svgStore.get('iconQrcode')"></div>
				<p class="scanner-instructions">{{ getString('qr_scanner_instructions') }}</p>
				<AppButton v-if="nativeScannerWaiting" class="mb-10" :text="getString('start_scan')" @click="launchNativeScanner" />
			</template>
			<template v-else-if="!scannerStarted">
				<div v-show="!errorMessage" class="qr-icon" v-html="svgStore.get('iconQrcode')"></div>
				<p class="scanner-instructions">{{ getString('qr_scanner_instructions') }}</p>
				<p class="scanner-instructions secondary">{{ getString('qr_scanner_instructions_2') }}</p>
				<AppButton class="mb-10" :text="getString('start_scan')" @click="detectCameras" :loading="loading" />
				<p v-if="errorMessage" class="error-message">{{ getString('error_x', [errorMessage]) }}</p>
			</template>
			<template v-else>
				<template v-if="cameras?.length > 1">
					<div v-show="!showCameraSelect" class="row flex-start">
						<AppButton small outline :text="getString('switch_camera')" @click="showCameraSelect = true" />
					</div>
					<AppSelect
						v-if="showCameraSelect"
						:values="cameraNames"
						:selected-index="selectedCameraIndex"
						@selected="onCameraSelected"
					/>
				</template>
				<div id="bottomsheet-qr-scanner-container" class="qr-container"></div>
			</template>
		</div>
	</div>
</template>

<script>
import {Html5Qrcode} from "html5-qrcode";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import API from "@/shared/helpers/API.js";
import {useLanguageStore, useSvgStore, useNotificationStore} from "@/shared/stores";
import {useCustomersStore, useCounterStore, useUserStore} from "@/console-new/stores";
import {isNative, getNative} from "@/shared/helpers/webapp";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		const counterStore = useCounterStore();
		const userStore = useUserStore();
		return {getString: languageStore.getString, svgStore, notificationStore, customersStore, counterStore, userStore};
	},
	name: "QrScanner",
	emits: ['scanner-closed', 'customer-scanned', 'consumer-link-preview', 'item-redeemed'],
	components: {ModalHeader, AppButton, AppSelect},
	data() {
		return {
			html5QrCode: null,
			scannerStarted: false,
			loading: false,
			errorMessage: null,
			cameras: [],
			selectedCameraIndex: 0,
			showCameraSelect: false,
			usingNativeScanner: false,
			nativeScannerWaiting: false
		}
	},
	computed: {
		cameraNames() {
			return this.cameras.map((camera, index) => {
				return camera?.label || this.getString('camera_device', [index + 1]);
			});
		}
	},
	mounted() {
		if(isNative()) {
			getNative().hasNativeQrScanner().then((hasScanner) => {
				if(hasScanner) {
					this.startNativeScanner();
				}
			}).catch(() => {
				// Native scanner not available, fall back to JS
			});
		}
	},
	methods: {
		startNativeScanner() {
			this.usingNativeScanner = true;
			window.qrScannerResult = (decodedText) => {
				this.nativeScannerWaiting = false;
				this.onScanSuccess(decodedText);
			};
			window.qrScannerCancelled = () => {
				this.$emit('scanner-closed');
			};
			window.qrScannerError = (error) => {
				console.error('[QrScanner] Error in native scanner:', error);
				if (error === 'invalid_url') {
					this.notificationStore.showNotification('qr-code-not-recognized');
					this.restartScanner();
					return;
				}
				this.nativeScannerWaiting = true;
				this.notificationStore.showNotification('generic-error');
			};
			this.launchNativeScanner();
		},
		launchNativeScanner() {
			this.nativeScannerWaiting = false;
			getNative().startQrScanner();
		},
		findBackCameraIndex() {
			const backKeywords = ['back', 'rear', 'environment'];
			const index = this.cameras.findIndex(camera => {
				const label = (camera.label || '').toLowerCase();
				return backKeywords.some(keyword => label.includes(keyword));
			});
			return index >= 0 ? index : 0;
		},
		detectCameras() {
			this.loading = true;
			this.errorMessage = null;
			Html5Qrcode.getCameras().then((cameras) => {
				this.loading = false;
				if(cameras && cameras?.length > 0) {
					this.cameras = cameras;
					this.selectedCameraIndex = this.findBackCameraIndex();
					this.startWithSelectedCamera();
				} else {
					this.errorMessage = this.getString('no_camera_found');
				}
			}).catch((err) => {
				this.loading = false;
				this.errorMessage = err?.message || String(err);
			});
		},
		startWithSelectedCamera() {
			this.scannerStarted = true;
			this.$nextTick(() => {
				this.startScanner(this.cameras[this.selectedCameraIndex].id);
			});
		},
		startScanner(cameraId, callback = null) {
			this.html5QrCode = new Html5Qrcode('bottomsheet-qr-scanner-container');
			this.html5QrCode.start(
				cameraId,
				{
					fps: 4,
					qrbox: {width: 250, height: 250},
					formatsToSupport: [0] // 0 = QR_CODE only
				},
				this.onScanSuccess,
				(errorMessage) => {
					// Ignore scan errors (no QR found in frame)
				}
			).then(() => {
				//Scanner started successfully
				if(callback) { callback(true); }
			}).catch((err) => {
				this.errorMessage = err?.message || String(err);
				this.scannerStarted = false;
				if(callback) { callback(false); }
			});

		},
		stopScanner(callback = null) {
			if(this.html5QrCode && this.html5QrCode.isScanning) {
				this.html5QrCode.stop().then(() => {
					//Scanner stopped
					if(callback) { callback(true); }
				}).catch((err) => {
					console.error('[QrScanner] Error stopping scanner:', err);
					if(callback) { callback(false); }
				});
			} else {
				if(callback) { callback(true); }
			}
		},
		onCameraSelected(index) {
			if(index === this.selectedCameraIndex) return;
			this.selectedCameraIndex = index;
			this.stopScanner(() => {
				this.$nextTick(() => {
					this.startScanner(this.cameras[index].id);
					this.showCameraSelect = false;
				});
			});
		},
		parseCustomerQrUrl(text) {
			try {
				let url = new URL(text);
				if (url.origin !== window.location.origin) 						return null;
				if(!url.pathname.endsWith('/c/')) 								return null;
				let t = url.searchParams.get('t');
				let b = url.searchParams.get('b');
				if(!b || !t) 													return null;
				if(t.length !== 8 || !(t.startsWith('c') || t.startsWith('p'))) return null;
				return {token: t, businessId: b};
			} catch(e) {
				return null;
			}
		},
		parseConsumerQrUrl(text) {
			try {
				let url = new URL(text);
				if (url.origin !== window.location.origin) 	return null;
				if (!url.pathname.endsWith('/c/')) 			return null;
				let ct = url.searchParams.get('ct');
				if (!ct || ct.length !== 8) 				return null;
				return {consumerToken: ct};
			} catch(e) {
				return null;
			}
		},
		parseRedeemQrUrl(text) {
			try {
				let url = new URL(text);
				let o = url.searchParams.get('o');
				if (url.origin !== window.location.origin) 	return null;
				if (!url.pathname.endsWith('/e/')) 			return null;
				if (!o || isNaN(o)) 				return null;
				let i = url.searchParams.get('i');
				let q = url.searchParams.get('q');
				let hasItem = i && q && !isNaN(i) && !isNaN(q);
				return {
					order_id: Number(o),
					order_item_id: hasItem ? Number(i) : null,
					quantity: hasItem ? Number(q) : null,
				};
			} catch(e) {
				return null;
			}
		},
		restartScanner() {
			if(this.usingNativeScanner) {
				this.nativeScannerWaiting = true;
				return;
			}
			this.$nextTick(() => {
				if(this.cameras?.[this.selectedCameraIndex]?.id) {
					this.startScanner(this.cameras[this.selectedCameraIndex].id);
				}
			});
		},
		onScanSuccess(decodedText) {
			if(this.usingNativeScanner) {
				this.handleScanResult(decodedText);
				return;
			}
			this.stopScanner(() => {
				this.handleScanResult(decodedText);
			});
		},
		handleScanResult(decodedText) {
			let parsedCustomer = this.parseCustomerQrUrl(decodedText);
			if(parsedCustomer) {
				if(Number(parsedCustomer.businessId) !== Number(this.userStore.currBusinessId)) {
					this.notificationStore.showNotification('qr-code-not-recognized');
					this.restartScanner();
					return;
				}
				this.customersStore.parseCustomerToken(parsedCustomer.token, ()=>{
					this.$emit('customer-scanned');
				}, this.restartScanner);
				return;
			}
			let parsedConsumer = this.parseConsumerQrUrl(decodedText);
			if(parsedConsumer) {
				this.customersStore.parseConsumerToken(parsedConsumer.consumerToken, (result) => {
					if(result?.linked) {
						this.$emit('customer-scanned');
					} else if(result && result.linked === false && result.consumer) {
						this.$emit('consumer-link-preview', {consumer: result.consumer, consumerToken: result.consumerToken});
					} else {
						this.restartScanner();
					}
				}, this.restartScanner);
				return;
			}
			let parsedRedeem = this.parseRedeemQrUrl(decodedText);
			if(parsedRedeem) {
				if(parsedRedeem.order_item_id) {
					this.redeemSingleItem(parsedRedeem);
				} else {
					const url = new URL(decodedText);
					if (url.origin !== window.location.origin) {
						this.notificationStore.showNotification('qr-code-not-recognized');
						this.restartScanner();
						return;
					}
					window.location.href = url.href;
				}
				return;
			}
			this.notificationStore.showNotification('qr-code-not-recognized');
			this.restartScanner();
		},
		redeemSingleItem(parsed) {
			let items = JSON.stringify([{order_item_id: parsed.order_item_id, redeemed: parsed.quantity}]);
			API.init().businessRedeemItems(this.userStore.currBusinessId, parsed.order_id, items, (response) => {
				if(response?.result === 'OK') {
					API.init().getOrderWithId(this.userStore.currBusinessId, parsed.order_id, (orderResponse) => {
						let item = orderResponse?.items?.find(i => Number(i.order_item_id) === Number(parsed.order_item_id));
						if(!item) {
							this.notificationStore.showNotification('item-not-found');
							this.restartScanner();
							return;
						}
						this.$emit('item-redeemed', {
							order_id: parsed.order_id,
							order_item_id: parsed.order_item_id,
							name: item?.name || null,
							picture: item?.picture || null,
							current_redeemed_quantity: parsed.quantity,
							quantity: item?.quantity,
							redeemed: item?.redeemed
						});
					});
				} else {
					let notificationName = response?.details === 'quantity_exceeded' ? 'item-already-redeemed' : 'generic-error';
					this.notificationStore.showNotification(notificationName);
					this.restartScanner();
				}
			});
		}
	},
	beforeUnmount() {
		delete window.qrScannerResult;
		delete window.qrScannerCancelled;
		delete window.qrScannerError;
		this.stopScanner();
	}
}
</script>

<style lang="scss" scoped>
.qr-scanner {

	.scanner-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		margin-top: 20px;

		.qr-container {
			width: 100%;
			max-width: 500px;
		}

		.error-message {
			color: var(--color-error);
			text-align: center;
			background-color: var(--color-error-bg, rgba(255, 0, 0, 0.1));
			padding: 12px 16px;
			border-radius: 8px;
			border: 1px solid var(--color-error);
			margin: 0;
		}

		.scanner-instructions {
			text-align: center;
			margin: 0;
			font-size: 16px;

			&.secondary {
				color: var(--color-txt-1);
				font-size: 14px;
			}
		}

		.qr-icon {
			width: 64px;
			height: 64px;

			:deep(svg) {
				width: 100%;
				height: 100%;

				.stroke {
					stroke: var(--color-txt);
				}

				.fill {
					fill: var(--color-txt);
				}
			}
		}
	}
}
</style>
